import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";
import {
  Box,
  Text,
  Flex,
  Paper,
  Button,
  Divider,
  ThemeIcon,
  ActionIcon,
  Grid,
  Stack,
} from "@mantine/core";
import {
  IconX,
  IconCircleCheck,
  IconUsers,
  IconBuilding,
  IconClock,
  IconFolder,
  IconInfoCircle,
  IconArrowRight,
} from "@tabler/icons-react";
import { PartnershipHandshakeIcon } from "@/components/icons/PartnershipHandshakeIcon";
import { useUserContext } from "@/context/UserContext";
import trpc from "@/shared/utils/trpc";
import type { CollaborationPaymentSuccessData } from "../hooks/useCollaborationEngagementPayment";

const NAVY = "#04305c";

function SuccessConfettiIcon() {
  const dots = [
    { top: 4, left: 8, color: "#F472B6", size: 6 },
    { top: 12, left: 52, color: "#60A5FA", size: 5 },
    { top: 2, left: 38, color: "#FBBF24", size: 4 },
    { top: 48, left: 4, color: "#34D399", size: 5 },
    { top: 44, left: 56, color: "#A78BFA", size: 6 },
    { top: 28, left: 62, color: "#F87171", size: 4 },
  ];

  return (
    <Box style={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
      {dots.map((d, i) => (
        <Box
          key={i}
          style={{
            position: "absolute",
            top: d.top,
            left: d.left,
            width: d.size,
            height: d.size,
            borderRadius: "50%",
            backgroundColor: d.color,
          }}
        />
      ))}
      <ThemeIcon
        size={64}
        radius="xl"
        color="green"
        variant="filled"
        style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
      >
        <IconCircleCheck size={36} stroke={2.5} />
      </ThemeIcon>
    </Box>
  );
}

function MatchBannerGraphic() {
  return (
    <Flex align="center" gap={6} style={{ flexShrink: 0 }}>
      <ThemeIcon size={40} radius="xl" variant="light" color="blue">
        <IconBuilding size={22} />
      </ThemeIcon>
      <Box
        style={{
          width: 28,
          height: 2,
          background: "linear-gradient(90deg, #93C5FD, #15803D, #FDE047)",
          borderRadius: 1,
          position: "relative",
        }}
      >
        <ThemeIcon
          size={22}
          radius="xl"
          color="green"
          variant="filled"
          style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
        >
          <IconCircleCheck size={12} stroke={2.5} />
        </ThemeIcon>
      </Box>
      <ThemeIcon size={40} radius="xl" variant="light" color="yellow">
        <IconBuilding size={22} />
      </ThemeIcon>
    </Flex>
  );
}

function CompanyChip({
  label,
  gtid,
  variant,
}: {
  label: string;
  gtid: string;
  variant: "yours" | "partner";
}) {
  return (
    <Flex direction="column" align="center" gap={6} style={{ flex: 1, minWidth: 0 }}>
      <ThemeIcon size={44} radius="xl" variant="light" color={variant === "yours" ? "blue" : "yellow"}>
        <IconBuilding size={24} />
      </ThemeIcon>
      <Text fz="xs" c="#64748B" ta="center">
        {label}
      </Text>
      <Text fz="sm" fw={700} c={NAVY} ta="center" style={{ wordBreak: "break-word" }}>
        {gtid}
      </Text>
    </Flex>
  );
}

type StepItem = {
  icon: typeof IconClock;
  title: string;
  description: string;
  active?: boolean;
};

function WhatsNextStepper({ steps }: { steps: StepItem[] }) {
  return (
    <Stack gap={0}>
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isLast = index === steps.length - 1;
        return (
          <Flex key={step.title} gap="md" align="stretch">
            <Flex direction="column" align="center" style={{ flexShrink: 0 }}>
              <ThemeIcon
                size={36}
                radius="xl"
                variant={step.active ? "filled" : "light"}
                color={step.active ? "blue" : "gray"}
              >
                <Icon size={18} stroke={1.75} />
              </ThemeIcon>
              {!isLast ? (
                <Box
                  w={2}
                  style={{
                    flex: 1,
                    minHeight: 28,
                    marginTop: 4,
                    marginBottom: 4,
                    borderLeft: "2px dashed #CBD5E1",
                  }}
                />
              ) : null}
            </Flex>
            <Box pb={isLast ? 0 : "lg"} pt={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fz="sm" fw={600} c="#0F172A" mb={4}>
                {step.title}
              </Text>
              <Text fz="xs" c="#64748B" lh={1.5}>
                {step.description}
              </Text>
            </Box>
          </Flex>
        );
      })}
    </Stack>
  );
}

type CollaborationPaymentSuccessContentProps = {
  paymentData: CollaborationPaymentSuccessData;
  onClose?: () => void;
  showCloseButton?: boolean;
};

export default function CollaborationPaymentSuccessContent({
  paymentData,
  onClose,
  showCloseButton = true,
}: CollaborationPaymentSuccessContentProps) {
  const navigate = useNavigate();
  const { userDetails } = useUserContext();
  const isMobile = useMediaQuery("(max-width: 768px)", false, { getInitialValueInEffect: true });
  const [partnerGtid, setPartnerGtid] = useState<string | null>(null);

  const bothPartiesPaid = paymentData.newStatus === "BOTH_PAID";
  const creditsCharged =
    typeof paymentData.creditsCharged === "number" ? paymentData.creditsCharged : 0;
  const creditsPendingBothParties = !bothPartiesPaid && creditsCharged === 0;

  const yourGtid = userDetails?.company?.gtid ?? "Your Company";
  const partnerLabel = partnerGtid ?? "Matched Company";

  useEffect(() => {
    if (!paymentData.matchId || !userDetails?.company?.id) return;
    let cancelled = false;

    trpc.collaboration.getById
      .query({ matchId: paymentData.matchId })
      .then((collab) => {
        if (cancelled || !collab) return;
        const isRequester = collab.requesterCompanyId === userDetails.company?.id;
        const other = isRequester ? collab.receiver : collab.requester;
        setPartnerGtid(other?.gtid ?? "Partner");
      })
      .catch(() => {
        if (!cancelled) setPartnerGtid("Partner");
      });

    return () => {
      cancelled = true;
    };
  }, [paymentData.matchId, userDetails?.company?.id]);

  const creditsLine = creditsPendingBothParties
    ? "Your payment step is recorded. Credits are charged from both companies when the other party pays."
    : creditsCharged > 0
      ? `${creditsCharged} credits have been used to activate this collaboration.`
      : "Your collaboration payment step is complete.";

  const notifyLine = bothPartiesPaid
    ? "Both parties have paid. Your collaboration workspace is ready."
    : "We've notified the other company. Waiting for them to accept and complete the process.";

  const bannerText = bothPartiesPaid
    ? "You're all set! Both companies have paid. You can open your workspace and start collaborating."
    : "You're all set! This is a mutual match. Once the other company completes payment, you'll both gain full access and can start collaborating.";

  const whatsNextSteps: StepItem[] = bothPartiesPaid
    ? [
        {
          icon: IconBuilding,
          title: "Full Access Unlocked",
          description: "View complete profiles, documents, chat, and partner readiness in your workspace.",
          active: true,
        },
        {
          icon: IconFolder,
          title: "Start Collaborating",
          description: "Share documents securely, complete questionnaires, and communicate with your partner.",
        },
      ]
    : [
        {
          icon: IconClock,
          title: "Waiting for Partner",
          description:
            "The other company needs to complete their payment. You'll be notified when they're ready.",
          active: true,
        },
        {
          icon: IconBuilding,
          title: "Full Access Unlocked",
          description:
            "Once both parties pay, you'll get full access to the workspace, documents, and communication tools.",
        },
        {
          icon: IconFolder,
          title: "Start Collaborating",
          description: "Share documents, chat, and work through partner readiness together.",
        },
      ];

  const handleGoToCollaborations = () => {
    onClose?.();
    if (bothPartiesPaid && paymentData.matchId) {
      navigate(`/collaborations/${paymentData.matchId}`);
      return;
    }
    navigate("/collaborations");
  };

  return (
    <Box>
      <Flex justify="space-between" align="flex-start" gap="md" mb="lg">
        <Flex
          gap="md"
          align={{ base: "center", sm: "flex-start" }}
          direction={{ base: "column", sm: "row" }}
          style={{ flex: 1, minWidth: 0 }}
        >
          <SuccessConfettiIcon />
          <Box miw={0} style={{ textAlign: isMobile ? "center" : "left" }}>
            <Text fw={700} fz={{ base: "xl", sm: 28 }} c={NAVY} lh={1.2}>
              Payment Successful!
            </Text>
            <Text fz={{ base: "xs", sm: "sm" }} c="#64748B" mt={8} lh={1.5}>
              {creditsLine}
            </Text>
            <Text fz={{ base: "xs", sm: "sm" }} c="#64748B" mt={4} lh={1.5}>
              {notifyLine}
            </Text>
          </Box>
        </Flex>
        {showCloseButton && onClose ? (
          <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Close" style={{ flexShrink: 0 }}>
            <IconX size={20} />
          </ActionIcon>
        ) : null}
      </Flex>

      <Paper
        radius="md"
        p="md"
        mb="lg"
        style={{ backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" }}
      >
        <Flex
          justify="space-between"
          align={{ base: "flex-start", sm: "center" }}
          gap="md"
          direction={{ base: "column", sm: "row" }}
        >
          <Flex gap="sm" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon size={40} radius="md" variant="light" color="green" style={{ flexShrink: 0 }}>
              <IconUsers size={22} />
            </ThemeIcon>
            <Text fz="sm" c="#166534" lh={1.5}>
              {bannerText}
            </Text>
          </Flex>
          <MatchBannerGraphic />
        </Flex>
      </Paper>

      <Grid gutter={{ base: "md", md: "lg" }} mb="lg">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="md">
            <Paper radius="md" p="md" withBorder style={{ borderColor: "#E2E8F0" }}>
              <Text fw={700} fz="sm" c="#0F172A" mb="md">
                Payment Details
              </Text>
              <Stack gap={0}>
                <Flex justify="space-between" align="center" py="sm" gap="md">
                  <Text fz="sm" c="#64748B">
                    Transaction ID
                  </Text>
                  <Text fz="sm" fw={700} c="#0F172A" ta="right" style={{ wordBreak: "break-all" }}>
                    {paymentData.transactionId}
                  </Text>
                </Flex>
                <Divider color="#E2E8F0" />
                <Flex justify="space-between" align="center" py="sm">
                  <Text fz="sm" c="#64748B">
                    Credits Debited
                  </Text>
                  <Text fz="sm" fw={700} c="#0F172A">
                    {creditsPendingBothParties ? "After both parties pay" : creditsCharged}
                  </Text>
                </Flex>
                <Divider color="#E2E8F0" />
                <Flex justify="space-between" align="center" py="sm">
                  <Text fz="sm" c="#64748B">
                    Date
                  </Text>
                  <Text fz="sm" fw={700} c="#0F172A" ta="right">
                    {paymentData.date}
                  </Text>
                </Flex>
              </Stack>
            </Paper>

            <Paper radius="md" p="md" withBorder style={{ borderColor: "#E2E8F0" }}>
              <Text fw={700} fz="sm" c="#0F172A" mb="md">
                Collaboration Summary
              </Text>
              <Flex
                align="center"
                justify="center"
                gap={{ base: "md", sm: "sm" }}
                direction={{ base: "column", sm: "row" }}
                wrap="wrap"
              >
                <CompanyChip label="Your Company" gtid={yourGtid} variant="yours" />
                <ThemeIcon size={36} radius="xl" color="green" variant="light" style={{ flexShrink: 0 }}>
                  <PartnershipHandshakeIcon size={20} />
                </ThemeIcon>
                <CompanyChip label="Matched Company" gtid={partnerLabel} variant="partner" />
              </Flex>
            </Paper>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper
            radius="md"
            p="md"
            h="100%"
            style={{ backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }}
          >
            <Text fw={700} fz="sm" c="#0F172A" mb="md">
              What&apos;s Next?
            </Text>
            <WhatsNextStepper steps={whatsNextSteps} />
          </Paper>
        </Grid.Col>
      </Grid>

      <Paper
        radius="md"
        p="md"
        style={{ backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }}
      >
        <Flex
          justify="space-between"
          align={{ base: "stretch", sm: "center" }}
          gap="md"
          direction={{ base: "column", sm: "row" }}
        >
          <Flex gap="sm" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon size={28} radius="xl" variant="light" color="blue" style={{ flexShrink: 0 }}>
              <IconInfoCircle size={16} />
            </ThemeIcon>
            <Text fz="xs" c="#475569" lh={1.5}>
              {bothPartiesPaid
                ? "We'll keep you updated with email and in-app notifications for workspace activity and messages."
                : "We'll keep you updated. You will receive an email and in-app notification once the other party completes the payment."}
            </Text>
          </Flex>
          <Button
            variant="default"
            radius="md"
            size={isMobile ? "md" : "sm"}
            rightSection={<IconArrowRight size={16} />}
            onClick={handleGoToCollaborations}
            style={{
              flexShrink: 0,
              borderColor: "#CBD5E1",
              color: NAVY,
              fontWeight: 600,
              ...(isMobile ? { width: "100%" } : {}),
            }}
          >
            Go to Collaborations
          </Button>
        </Flex>
      </Paper>
    </Box>
  );
}
